import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { SPP_PRODUCT_NAME } from '../../constants/DataTestIdAttributes';
import { breakpoint, ContentSpacing } from 'elc-base-theme';
import { Heading4, Heading5, Heading3 } from 'elc-typography';
import { ProductDisplayNameTheme, ProductSublineNameTheme } from '../../../theme/default-theme';
import { Product } from '../../domain/entities/Product';
import { Sku } from '../../domain/entities/Sku';
import { classes } from '../../utils/Classes';
import { ProductShortDescription } from '../common/ProductShortDescription';
import { IShortDescriptionConfig } from '../../interfaces/IProdcat';

export interface IProductFullBaseInfoConfig {
    showSubDisplayName?: boolean;
    shortDescriptionLocation?: IShortDescriptionConfig;
}

export interface IProductFullBaseInfo {
    product: Product;
    selectedSku: Sku;
    config: IProductFullBaseInfoConfig;
    renderShortDescription?: boolean;
}

const ProductFullBaseInfoWrapper = styled.div`
    text-align: center;
    padding: 0 ${ContentSpacing.space8};
    ${breakpoint('desktop')`
        text-align: left;
        [dir='rtl'] & {
            text-align: right;
        }
    `};
`;

const ProductInfoWrapper = styled.div`
    margin-top: ${ContentSpacing.space20};
`;

const ProductNameWrapper = styled.div`
    margin: ${ContentSpacing.space12} 0;
    ${breakpoint('desktop')`
        margin: ${ContentSpacing.space12} 0 0;
    `};
`;

const ProductDisplayName = styled(Heading3)`
    ${ProductDisplayNameTheme}
    margin: ${ContentSpacing.space16} 0;
`.withComponent('h1');

const SublineName = styled(Heading5)`
    ${ProductSublineNameTheme}
    margin: ${ContentSpacing.space8} 0;
`;

const ProductSubheader = styled(Heading4)`
    ${ProductSublineNameTheme}
    margin: ${ContentSpacing.space8} 0;
`;

export const ProductFullBaseInfo: React.SFC<IProductFullBaseInfo> = observer(
    (props: IProductFullBaseInfo) => {
        const productFullBaseInfoClassNames = classes('product-full-base-info');
        const productInfoWrapperClassNames = classes('product-info-wrapper');
        const productNameWrapperClassNames = classes('product-name-wrapper');
        const productSubDisplayNameClassNames = classes('product-sub-display-name');
        const productDisplayNameLinkClassNames = classes('product-display-name-link');
        const productSubheaderClassNames = classes('product-subheader');

        const { product, config, renderShortDescription } = props;

        const { showSubDisplayName, shortDescriptionLocation } = config;
        const { displayName, subDisplayName, shortDescription, subHeader } = product;

        //@todo MPPFIND-2679: remove shortDescriptionLocation logic once Drupal updated
        const shouldRenderShortDescription =
            (shortDescriptionLocation &&
                shortDescriptionLocation.product_full &&
                shortDescription) ||
            renderShortDescription;

        return (
            <ProductFullBaseInfoWrapper className={productFullBaseInfoClassNames}>
                <ProductInfoWrapper className={productInfoWrapperClassNames}>
                    <ProductNameWrapper className={productNameWrapperClassNames}>
                        <ProductDisplayName
                            className={productDisplayNameLinkClassNames}
                            dangerouslySetInnerHTML={{ __html: displayName }}
                            data-test-id={SPP_PRODUCT_NAME}
                        />
                    </ProductNameWrapper>
                    {subHeader && (
                        <ProductSubheader className={productSubheaderClassNames}>
                            {subHeader}
                        </ProductSubheader>
                    )}
                    {showSubDisplayName && (
                        <SublineName className={productSubDisplayNameClassNames}>
                            {subDisplayName}
                        </SublineName>
                    )}
                    {shouldRenderShortDescription && (
                        <ProductShortDescription description={shortDescription} />
                    )}
                </ProductInfoWrapper>
            </ProductFullBaseInfoWrapper>
        );
    }
);
